import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation } from '@apollo/client'
import { Box, CircularProgress } from '@mui/material'
import { STAFF_LINK } from './openInWizard'
import { T } from './onboardingDesign'
import { Notice } from './onboardingUi'

/**
 * The merchant sign-up form itself (app.rushmyfood.com, ?frame=1), framed in
 * the admin site with a staff link: the same form, look and saving as the
 * restaurant's. Used by a super admin's application page and an agent's.
 *
 * The form tells the page when it saves or creates the application
 * (postMessage from rushmyfood-web screens/PartnerSignup), and reloads when
 * the page asks it to (refreshForm). Staff links last 12 hours: when the
 * form's has expired it asks for a new one ('renew'), and the page sends it a
 * fresh token without reloading it, so nothing typed is lost.
 *
 *   id        the application, or 'new' to start one
 *   enabled   false until the page knows it can be framed
 *   view      a signed or rejected one, shown locked (super admin only)
 *   onSaved   the form saved (debounced)
 *   onCreated the form created the application: (id) => …
 *   created   optional ref the page reads too (an id created in this frame)
 */
export function useApplicationFrame({ id, enabled = true, view = false, onSaved, onCreated, created }) {
  const isNew = id === 'new'
  const frameRef = useRef(null)
  const [src, setSrc] = useState(null)
  const [error, setError] = useState(null)
  const [getStaffLink, { loading }] = useMutation(STAFF_LINK)
  const getLinkRef = useRef(getStaffLink)
  getLinkRef.current = getStaffLink
  // An application created in this frame keeps its frame (no reload mid-save).
  const ownCreated = useRef(null)
  const createdHere = created || ownCreated
  const savedRef = useRef(onSaved)
  const createdRef = useRef(onCreated)
  savedRef.current = onSaved
  createdRef.current = onCreated
  // Which application (and mode) the frame's link is for. A link is fetched
  // once per application: a new one would reload the form and lose what is
  // typed in the open step.
  const linkFor = useRef(null)
  const want = `${isNew ? 'new' : id}|${view ? 'view' : 'edit'}`
  // For renewing the form's token: the application it has open now.
  const current = useRef({ id, isNew, view })
  current.current = { id, isNew, view }

  useEffect(() => {
    if (!enabled) return undefined
    if (!isNew && createdHere.current === String(id)) {
      linkFor.current = want
      return undefined
    }
    if (linkFor.current === want) return undefined
    linkFor.current = want
    // Another application: never show the last one's form meanwhile.
    setSrc(null)
    let alive = true
    setError(null)
    // embed=0: a menu preview in this tab pins the web app's embed mode in its
    // sessionStorage, which would bounce /partner-signup to that restaurant.
    getLinkRef.current({ variables: { id: isNew ? null : id, view: !!view } })
      .then(({ data }) => { if (alive) setSrc(`${data.merchantStaffLink}&frame=1&embed=0`) })
      .catch(e => {
        if (!alive) return
        linkFor.current = null
        setError(e.message)
      })
    return () => { alive = false }
  }, [want, id, isNew, enabled, view, createdHere])

  useEffect(() => {
    let timer = null
    const onMessage = (e) => {
      if (!frameRef.current || e.source !== frameRef.current.contentWindow) return
      const m = e.data || {}
      if (m.source !== 'rmf-signup') return
      if (m.type === 'created' && m.id) {
        createdHere.current = String(m.id)
        if (createdRef.current) createdRef.current(m.id)
        return
      }
      if (m.type === 'saved') {
        clearTimeout(timer)
        timer = setTimeout(() => { if (savedRef.current) savedRef.current() }, 1200)
        return
      }
      if (m.type === 'renew') {
        const { id: openId, isNew: starting, view: viewing } = current.current
        const appId = createdHere.current || (starting ? null : openId)
        if (!appId) return
        const frameWindow = frameRef.current.contentWindow
        getLinkRef.current({ variables: { id: appId, view: !!viewing } })
          .then(({ data }) => {
            const link = data.merchantStaffLink
            const token = new URLSearchParams(link.split('?')[1] || '').get('staff')
            if (token) frameWindow.postMessage({ source: 'rmf-admin', type: 'token', token }, new URL(link).origin)
          })
          .catch(() => { /* the form shows the save error */ })
      }
    }
    window.addEventListener('message', onMessage)
    return () => {
      window.removeEventListener('message', onMessage)
      clearTimeout(timer)
    }
  }, [])

  // The page changed the application (its fees): the form reloads its open step.
  const refreshForm = useCallback(() => {
    if (!frameRef.current || !src) return
    frameRef.current.contentWindow.postMessage({ source: 'rmf-admin', type: 'refresh' }, new URL(src).origin)
  }, [src])

  return { frameRef, src, error, loading: loading && !src, refreshForm, createdHere }
}

/** The framed form, in a card. */
export function FormFrame({ frame, sx }) {
  const { src, frameRef, loading, error } = frame
  return (
    <Box sx={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: '20px', overflow: 'hidden', position: 'relative', ...sx }}>
      {error ? (
        <Box sx={{ p: 3 }}><Notice severity="error">{error}</Notice></Box>
      ) : !src || loading ? (
        <Box sx={{ height: '100%', minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress sx={{ color: T.red }} /></Box>
      ) : (
        // key: a new link (another application) is a new frame. Changing only
        // the part after # would not reload it, and the form would keep the
        // application it had open.
        <Box component="iframe" key={src} ref={frameRef} title="Application form" src={src} sx={{ width: '100%', height: '100%', border: 0, display: 'block', background: T.pageBg }} />
      )}
    </Box>
  )
}
